import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../services/auth';
import { StorageService } from '../../services/storage';
import { MenuService } from '../../services/menu';
import { ToastService } from '../../services/toast';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PasswordModule, ButtonModule],
  templateUrl: './change-password.html',
  styleUrls: ['./change-password.css'],
})
export class ChangePassword {
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private storage = inject(StorageService);
  private menu = inject(MenuService);
  private toast = inject(ToastService);
  private router = inject(Router);

  saving = signal(false);

  form: FormGroup = this.fb.group(
    {
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
    },
    { validators: [this.passwordsMatch, this.newDiffersFromCurrent] },
  );

  // Field values mirrored into signals so the strength meter + checklist react live.
  private cur = signal('');
  pwd = signal('');
  private confirmVal = signal('');

  constructor() {
    this.form.get('currentPassword')!.valueChanges.subscribe((v) => this.cur.set(v ?? ''));
    this.form.get('newPassword')!.valueChanges.subscribe((v) => this.pwd.set(v ?? ''));
    this.form.get('confirmPassword')!.valueChanges.subscribe((v) => this.confirmVal.set(v ?? ''));
  }

  /** Live requirements checklist. */
  readonly checks = computed(() => {
    const p = this.pwd();
    return {
      len: p.length >= 6,
      mix: /[a-zA-Z]/.test(p) && /\d/.test(p),
      diff: !!p && p !== this.cur(),
      match: !!p && p === this.confirmVal(),
    };
  });

  /** 1–4 strength score: length, longer length, letters+digits, symbols/mixed case. */
  readonly strength = computed(() => {
    const p = this.pwd();
    if (!p) return { score: 0, label: '', color: '' };
    let s = 0;
    if (p.length >= 6) s++;
    if (p.length >= 10) s++;
    if (/[a-zA-Z]/.test(p) && /\d/.test(p)) s++;
    if (/[^a-zA-Z0-9]/.test(p) || (/[a-z]/.test(p) && /[A-Z]/.test(p))) s++;
    const levels = [
      { label: 'Weak', color: '#dc2626' },
      { label: 'Weak', color: '#dc2626' },
      { label: 'Fair', color: '#d97706' },
      { label: 'Good', color: '#0d9488' },
      { label: 'Strong', color: '#16a34a' },
    ];
    const score = Math.max(1, s);
    return { score, ...levels[score] };
  });

  /** new === confirm */
  private passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const n = group.get('newPassword')?.value;
    const c = group.get('confirmPassword')?.value;
    return n && c && n !== c ? { mismatch: true } : null;
  }

  /** new !== current */
  private newDiffersFromCurrent(group: AbstractControl): ValidationErrors | null {
    const cur = group.get('currentPassword')?.value;
    const n = group.get('newPassword')?.value;
    return cur && n && cur === n ? { sameAsOld: true } : null;
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { currentPassword, newPassword } = this.form.value;
    this.saving.set(true);
    this.auth.changePassword(currentPassword, newPassword).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to change password');
          return;
        }
        this.toast.success('Password changed. Please sign in again.');
        this.logout();
      },
      error: () => this.saving.set(false),
    });
  }

  /** Always sign out after a successful change so the new credentials must be used. */
  private logout() {
    this.auth.logout().subscribe({
      next: () => this.finishLogout(),
      error: () => this.finishLogout(),
    });
  }

  private finishLogout() {
    this.storage.clearAll();
    this.menu.reset();
    this.router.navigate(['/login']);
  }
}
